<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useNotificationStore, type NotificationType } from '@/stores/notification'
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  InformationCircleIcon
} from '@heroicons/vue/24/solid'
import { onMounted, ref, watch } from 'vue'

const notificationStore = useNotificationStore()
const { notification } = storeToRefs(notificationStore)

let dismissTimer: ReturnType<typeof setTimeout> | null = null
let updateTimer: ReturnType<typeof setInterval> | null = null
const remainingTime = ref(0)

const getTypeStyles = (type: NotificationType) => {
  const styles = {
    error: {
      bgColor: 'bg-red-50 dark:bg-red-900/20',
      borderColor: 'border-red-200 dark:border-red-800',
      headerBorderColor: 'border-red-100 dark:border-red-900',
      bgLight: 'bg-red-100 dark:bg-red-900',
      textColor: 'text-red-600 dark:text-red-200',
      buttonColor: 'bg-red-600 hover:bg-red-700',
      icon: ExclamationTriangleIcon
    },
    warning: {
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
      borderColor: 'border-yellow-200 dark:border-yellow-800',
      headerBorderColor: 'border-yellow-100 dark:border-yellow-900',
      bgLight: 'bg-yellow-100 dark:bg-yellow-900',
      textColor: 'text-yellow-600 dark:text-yellow-200',
      buttonColor: 'bg-yellow-600 hover:bg-yellow-700',
      icon: ExclamationCircleIcon
    },
    success: {
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      borderColor: 'border-green-200 dark:border-green-800',
      headerBorderColor: 'border-green-100 dark:border-green-900',
      bgLight: 'bg-green-100 dark:bg-green-900',
      textColor: 'text-green-600 dark:text-green-200',
      buttonColor: 'bg-green-600 hover:bg-green-700',
      icon: CheckCircleIcon
    },
    info: {
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      borderColor: 'border-blue-200 dark:border-blue-800',
      headerBorderColor: 'border-blue-100 dark:border-blue-900',
      bgLight: 'bg-blue-100 dark:bg-blue-900',
      textColor: 'text-blue-600 dark:text-blue-200',
      buttonColor: 'bg-blue-600 hover:bg-blue-700',
      icon: InformationCircleIcon
    }
  }
  return styles[type]
}

const handleClose = () => {
  clearDismissTimer()
  clearUpdateTimer()
  notificationStore.clearNotification()
}

const clearDismissTimer = () => {
  if (dismissTimer) {
    clearTimeout(dismissTimer)
    dismissTimer = null
  }
}

const clearUpdateTimer = () => {
  if (updateTimer) {
    clearInterval(updateTimer)
    updateTimer = null
  }
}

const startDismissTimer = () => {
  if (
    notification.value.isVisible &&
    notification.value.duration &&
    notification.value.duration > 0
  ) {
    remainingTime.value = notification.value.duration
    clearUpdateTimer()

    // Update remaining time every 50ms for smooth progress bar
    updateTimer = setInterval(() => {
      remainingTime.value = Math.max(0, remainingTime.value - 50)
    }, 50)

    dismissTimer = setTimeout(() => {
      handleClose()
    }, notification.value.duration)
  }
}

onMounted(() => {
  startDismissTimer()
})

watch(
  () => notification.value.isVisible,
  (isVisible) => {
    if (isVisible) {
      startDismissTimer()
    } else {
      clearDismissTimer()
      clearUpdateTimer()
    }
  }
)

const watcherFn = () => {
  if (
    notification.value.isVisible &&
    notification.value.duration &&
    notification.value.duration > 0
  ) {
    clearDismissTimer()
    startDismissTimer()
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="slide">
      <div
        v-if="notification.isVisible"
        class="fixed top-4 right-4 z-50 max-w-sm"
        @mouseenter="clearDismissTimer"
        @mouseleave="watcherFn"
      >
        <div
          :class="[
            'rounded-lg shadow-lg border p-4 backdrop-blur-sm overflow-hidden',
            'bg-white/95 dark:bg-gray-900/95',
            getTypeStyles(notification.type).borderColor
          ]"
        >
          <div class="flex items-start gap-3">
            <div
              :class="[
                'flex-shrink-0 flex items-center justify-center h-6 w-6 rounded-full',
                getTypeStyles(notification.type).bgLight
              ]"
            >
              <component
                :is="getTypeStyles(notification.type).icon"
                :class="['h-4 w-4', getTypeStyles(notification.type).textColor]"
              />
            </div>
            <div class="flex-1">
              <h3 class="text-sm font-semibold text-gray-900 dark:text-white">
                {{ notification.title }}
              </h3>
              <p class="text-xs text-gray-600 dark:text-gray-300 mt-1">
                {{ notification.message }}
              </p>
              <p
                v-if="notification.statusCode"
                class="text-xs text-gray-500 dark:text-gray-400 mt-1"
              >
                Error code: {{ notification.statusCode }}
              </p>
            </div>
            <button
              @click="handleClose"
              class="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <XMarkIcon class="w-4 h-4" />
            </button>
          </div>

          <div
            v-if="notification.duration && notification.duration > 0"
            class="mt-3 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden"
          >
            <div
              :class="[
                'h-full rounded-full transition-all',
                notification.type === 'error'
                  ? 'bg-red-500'
                  : notification.type === 'warning'
                    ? 'bg-yellow-500'
                    : notification.type === 'success'
                      ? 'bg-green-500'
                      : 'bg-blue-500'
              ]"
              :style="{
                width: `${Math.max(0, (remainingTime / (notification.duration || 1)) * 100)}%`
              }"
            ></div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from {
  transform: translateX(400px);
  opacity: 0;
}

.slide-leave-to {
  transform: translateX(400px);
  opacity: 0;
}
</style>
